import React from "react";

/**
 * AfriMarket brand logomark — "Adinkra Knot" (Nyansapo / wisdom-knot).
 *
 * Two interlaced gold loops symbolising the connection between Africa and the
 * diaspora, on a midnight backdrop, with an italic "A" centring the mark and
 * four cream "knot" pegs at the crossings. Designed to read crisply at any
 * size from 16px favicon to 200px hero mark.
 *
 * Props:
 *   - size: mark edge length in px (default 48)
 *   - withWordmark: render the "AfriMarket" wordmark beside the mark
 *   - wordmarkClassName: Tailwind override for the wordmark span
 */
const Logo = ({ size = 48, withWordmark = false, wordmarkClassName = "", className = "" }) => {
  const id = React.useId();
  const bgId = `lg-bg-${id}`;
  const goldId = `lg-gold-${id}`;
  return (
    <div className={`inline-flex flex-col items-center gap-1 ${className}`} aria-label="AfriMarket">
      <svg
        width={size}
        height={size}
        viewBox="0 0 96 96"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-hidden={withWordmark ? "true" : "false"}
      >
        <defs>
          <linearGradient id={bgId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#22343C"/>
            <stop offset="100%" stopColor="#0F1A20"/>
          </linearGradient>
          <linearGradient id={goldId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F4D17A"/>
            <stop offset="50%" stopColor="#D9923B"/>
            <stop offset="100%" stopColor="#A06A20"/>
          </linearGradient>
        </defs>
        <rect width="96" height="96" rx="20" fill={`url(#${bgId})`}/>
        {/* Soft concentric halos */}
        <circle cx="48" cy="48" r="36" fill="none" stroke="#D9923B" strokeWidth="0.5" opacity="0.32"/>
        <circle cx="48" cy="48" r="32" fill="none" stroke="#D9923B" strokeWidth="0.5" opacity="0.18"/>
        {/* Interlaced wisdom-knot — two gold loops */}
        <g stroke={`url(#${goldId})`} strokeWidth="3.6" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path d="M48 18 C68 18 78 38 68 48 C58 58 38 58 28 48 C18 38 28 18 48 18 Z"/>
          <path d="M48 78 C28 78 18 58 28 48 C38 38 58 38 68 48 C78 58 68 78 48 78 Z"/>
        </g>
        {/* Crossing pegs */}
        <circle cx="32.5" cy="44" r="1.9" fill="#FAF8F5"/>
        <circle cx="63.5" cy="44" r="1.9" fill="#FAF8F5"/>
        <circle cx="32.5" cy="52" r="1.9" fill="#FAF8F5"/>
        <circle cx="63.5" cy="52" r="1.9" fill="#FAF8F5"/>
        {/* Centre monogram — AFMG stacked vertically (AfriMarket Group) */}
        <text
          x="48"
          textAnchor="middle"
          fontFamily="'Cormorant Garamond', Georgia, serif"
          fontWeight="700"
          fontSize="13"
          fill="#FAF8F5"
        >
          <tspan x="48" y="31">A</tspan>
          <tspan x="48" y="45">F</tspan>
          <tspan x="48" y="59">M</tspan>
          <tspan x="48" y="73">G</tspan>
        </text>
      </svg>
      {withWordmark && (
        <span className={`font-display font-semibold tracking-[0.14em] leading-none ${wordmarkClassName || "text-[0.6rem]"}`}>
          Afri<span className="relative">M<span className="absolute -top-[0.05em] right-[0.18em] w-[0.18em] h-[0.18em] bg-[#D9923B] rounded-[1px]" aria-hidden="true"/></span>arket
        </span>
      )}
    </div>
  );
};

export default Logo;
